import { For, Show, createMemo, createSignal, onCleanup, onMount } from "solid-js";
import { Portal } from "solid-js/web";
import { useNavigate } from "@solidjs/router";
import { useApi, isOk, type NotificationDto } from "../api";
import { notificationIcon, notificationLink, notificationText, notificationTone, useNotifications } from "../services/notificationStore";
import { useTranslation } from "../hooks/useTranslation";
import { useVisibilityPoll } from "../hooks/useVisibilityPoll";
import Text from "./Text";
import { RelativeTime } from "./RelativeTime";
import styles from "./NotificationBell.module.css";

const NOTIFICATION_POLL_MS = 30_000;

export default function NotificationBell() {
  const { notifications, setNotifications, open, setOpen, unreadCount } = useNotifications();
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();

  const reloadNotifications = async () => {
    const res = await api.notification.list();
    if (isOk(res.data)) setNotifications(res.data.value.items);
  };

  onMount(() => {
    void reloadNotifications();
    useVisibilityPoll(() => void reloadNotifications(), NOTIFICATION_POLL_MS);
  });

  const markEverythingRead = async () => {
    if (!notifications().some((n) => !n.isRead)) return;
    setNotifications(notifications().map((n) => ({ ...n, isRead: true })));
    await api.notification.readAll();
  };

  const clearEverything = async () => {
    setNotifications([]);
    await api.notification.dismissAll();
    void reloadNotifications();
  };

  const dismissNotification = async (notification: NotificationDto) => {
    setNotifications(notifications().filter((n) => n.id !== notification.id));
    await api.notification.dismiss(notification.id);
  };

  const openResource = (link: string) => {
    setOpen(false);
    navigate(link);
  };

  let panelRef: HTMLDivElement | undefined;
  let triggerRef: HTMLButtonElement | undefined;

  const count = createMemo(() => unreadCount());
  const [anchor, setAnchor] = createSignal<{ top: number; right: number } | null>(null);

  const updateAnchor = () => {
    if (!triggerRef) return;
    const r = triggerRef.getBoundingClientRect();
    const vw = window.innerWidth;
    const panelWidth = Math.min(360, vw - 16);
    const desiredRight = vw - r.right;
    const maxRight = vw - panelWidth - 8;
    const right = Math.max(8, Math.min(desiredRight, maxRight));
    setAnchor({ top: r.bottom + 6, right });
  };

  function toggle() {
    const next = !open();
    if (next) updateAnchor();
    setOpen(next);
    if (next) {
      setTimeout(() => void markEverythingRead(), 800);
    }
  }

  function handleDocClick(e: MouseEvent) {
    if (!open()) return;
    // composedPath, not contains: a dismissed row is already detached when this document listener runs.
    const path = e.composedPath();
    if (panelRef && path.includes(panelRef)) return;
    if (triggerRef && path.includes(triggerRef)) return;
    setOpen(false);
  }

  function handleEscape(e: KeyboardEvent) {
    if (e.key === "Escape" && open()) setOpen(false);
  }

  onMount(() => {
    document.addEventListener("click", handleDocClick);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", updateAnchor);
    window.addEventListener("scroll", updateAnchor, true);
  });
  onCleanup(() => {
    document.removeEventListener("click", handleDocClick);
    document.removeEventListener("keydown", handleEscape);
    window.removeEventListener("resize", updateAnchor);
    window.removeEventListener("scroll", updateAnchor, true);
  });

  return (
    <div class={styles.wrap}>
      <button
        ref={triggerRef}
        class={styles.trigger}
        onClick={toggle}
        aria-label={count() > 0 ? t("NOTIFICATIONS_UNREAD", count()) : t("NOTIFICATIONS")}
        aria-expanded={open()}
      >
        <i class="fa-solid fa-bell" />
        <Show when={count() > 0}>
          <span class={styles.badge}>{count() > 99 ? "99+" : count()}</span>
        </Show>
      </button>
      <Show when={open() && anchor()}>
        {(a) => (
          <Portal mount={document.body}>
            <div
              ref={panelRef}
              class={styles.panel}
              role="dialog"
              aria-label={t("NOTIFICATIONS")}
              style={{
                position: 'fixed',
                top: `${a().top}px`,
                right: `${a().right}px`,
                left: 'auto',
              }}
            >
              <div class={styles.head}>
                <strong><Text val="NOTIFICATIONS" /></strong>
                <Show when={notifications().length > 0}>
                  <button class={styles.clearBtn} onClick={() => void clearEverything()}>
                    <i class="fa-solid fa-trash" /> <Text val="CLEAR" />
                  </button>
                </Show>
              </div>
              <Show
                when={notifications().length > 0}
                fallback={
                  <div class={styles.empty}>
                    <i class="fa-solid fa-bell-slash" />
                    <p><Text val="NOTIFICATIONS_EMPTY" /></p>
                  </div>
                }
              >
                <ul class={styles.list}>
                  <For each={notifications()}>
                    {(n) => {
                      const text = () => notificationText(n, t);
                      const link = notificationLink(n);
                      const content = () => (
                        <>
                          <div class={styles.title}>{text().title}</div>
                          <div class={styles.text}>{text().body}</div>
                          <div class={styles.meta}>
                            <RelativeTime value={n.lastSeenAtUtc} />
                            <Show when={n.occurrenceCount > 1}>
                              {" · "}{t("NOTIFICATION_OCCURRENCES", n.occurrenceCount)}
                            </Show>
                          </div>
                        </>
                      );
                      return (
                        <li class={`${styles.item} ${styles[`level-${notificationTone(n)}`]} ${n.isRead ? "" : styles.unread}`}>
                          <i class={`fa-solid ${notificationIcon(n)}`} aria-hidden="true" />
                          <Show when={link} fallback={<div class={styles.body}>{content()}</div>}>
                            {(href) => (
                              <button type="button" class={styles.linkBody} onClick={() => openResource(href())}>
                                {content()}
                              </button>
                            )}
                          </Show>
                          <button
                            type="button"
                            class={styles.dismissBtn}
                            title={t("NOTIFICATION_DISMISS")}
                            aria-label={t("NOTIFICATION_DISMISS")}
                            onClick={() => void dismissNotification(n)}
                          >
                            <i class="fa-solid fa-xmark" />
                          </button>
                        </li>
                      );
                    }}
                  </For>
                </ul>
              </Show>
            </div>
          </Portal>
        )}
      </Show>
    </div>
  );
}
